import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { browserHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';

import {
	addToPartsCart,
	getCyberSource,
	getOnlinePricingForParts,
	getTax,
	processPartsOrder,
	resetAvailabilityToCart,
	resetPricesToCart,
	saveCreateOrderPayload,
	saveToCart,
	updateCartOrderPoints,
	validatePriceSubType
} from '../../../redux/actions';
import { PNET_APP_QUERY_PARAM, shippingMethods, shippingPriorities } from '../../common/partsNetConstants';
import CollapsibleSection from '../../common/collapsibleSection';
import { routes } from '../../../redux/api/routes';
import GenericOverlayDialog from '../../dialogs/genericOverlayDialog';
import { DATE_TIME_FORMAT } from '../../common/messageConstants';

const { cyberSourceRedirectURL, cyberSourceURL, accessKey, profileId } = routes;

const PartsNetCheckout = props => {
	const {
		user,
		cart,
		order: { createOrderPayload }
	} = useSelector(state => state);

	const [fields, setFields] = useState({
		customerPONumber: '',
		phoneNumber: '',
		emailId: '',
		freightForwarder: createOrderPayload && createOrderPayload.freightForwarder,

		orderType: createOrderPayload && createOrderPayload.orderType,
		requestedShipDate: createOrderPayload && createOrderPayload.requestedShipDate,
		shippingMethod: createOrderPayload && createOrderPayload.shippingMethod,
		shipSet: createOrderPayload && createOrderPayload.shipSet,

		pricesOnPackingSlip: 'N',
		customerInstructions: '',
		shippingInstructions: ''
	});

	const { shippingMethod, orderType } = fields;

	const [errors, setErrors] = useState({
		customerPONumber: false,
		phoneNumber: false,
		emailId: false,
		freightForwarder: false,

		orderType: false,
		requestedShipDate: false,
		shippingMethod: false,
		shipSet: false,

		pricesOnPackingSlip: false,
		customerInstructions: false,
		shippingInstructions: false
	});
	const [dialog, setDialog] = useState();
	const [paymentError, setPaymentError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);

	const [availableFreightForwarders, setAvailableFreightForwarders] = useState(() => (createOrderPayload ? createOrderPayload.availableFreightForwarders : []));

	const [shippingAddress, setShippingAddress] = useState();
	const [billingAddress, setBillingAddress] = useState();

	const [items, setItems] = useState(() => (createOrderPayload && createOrderPayload.items) || []);
	const [handlingCharge, setHandlingCharge] = useState(() => (createOrderPayload && createOrderPayload.handlingCharge) || '0');
	const [taxAmount, setTaxAmount] = useState(0);

	const [paymentDetails, setPaymentDetails] = useState({});
	const paymentDetailsRef = useRef({
		skip_decision_manager: true,
		profile_id: profileId,
		access_key: accessKey,
		// transaction_uuid: uuidv4(),
		signed_field_names:
			'access_key,profile_id,transaction_uuid,signed_field_names,signed_date_time,locale,transaction_type,reference_number,amount,currency,override_custom_receipt_page,override_custom_cancel_page,skip_decision_manager,payment_method,bill_to_forename,bill_to_surname,bill_to_middleinitial,bill_to_address_line1,bill_to_address_line2,bill_to_address_city,bill_to_address_state,bill_to_address_country,bill_to_address_postal_code,bill_to_email,bill_to_company_name',
		locale: 'en',
		transaction_type: 'create_payment_token',
		currency: 'USD',
		// signed_date_time: signed_date_time,
		reference_number: new Date().getTime() + '-1001',
		bill_to_address_country: 'US',
		override_custom_receipt_page: cyberSourceRedirectURL,
		override_custom_cancel_page: cyberSourceRedirectURL,

		//Dynamic Values
		amount: '',
		payment_method: 'card',

		bill_to_email: '',
		bill_to_forename: '',
		bill_to_middleinitial: '',
		bill_to_surname: '',
		bill_to_company_name: '',
		bill_to_address_line1: '',
		bill_to_address_line2: '',
		bill_to_address_city: '',
		bill_to_address_postal_code: '',
		bill_to_address_state: '',
		isReadyForPayment: false,
		isPaymentConfirmed: false
	});

	const dispatch = useDispatch();

	const [error, setError] = useState();
	const [bagItemVisibility, setBagItemVisibility] = useState({});

	const hasPricingAccess = props.isResponsibilityExists('Price');
	const hasCreateOrderAccess = props.isResponsibilityExists('Create New Order');

	const cartSubTotal = useMemo(() => {
		return items.reduce((acc, item) => {
			const itemTotal = Number(item.extendedPrice || 0);
			const bagItemsTotal = (item.bagItems || [])
				.filter(bagItem => bagItemVisibility[`${item.id}-${bagItem.name}`]) // Filter visible bag items
				.reduce(
					(bagAcc, bagItem) => bagAcc + Number(bagItem.extendedPrice || 0),
					0
		);
			return acc + itemTotal + bagItemsTotal;
		}, 0);
		}, [items, bagItemVisibility]);
	const orderTotal = (+cartSubTotal + +handlingCharge + +taxAmount).toFixed(2);

	const getShippingMethod = () =>
		shippingMethods[props.isInternationalPartsCustomer ? 'international' : 'domestic'][fields.orderType === 'R' ? 'regular' : 'emergency'].find(
			shippingMethodItem => shippingMethodItem.displayName === fields.shippingMethod
		);

	const checkPriceSubTypeExcluded = (priceSubType, callback) => {
		dispatch(
			validatePriceSubType(
				`requestType=validatePriceSubType&appName=PNET&priceSubType=${encodeURIComponent(priceSubType)}`,
				response => {
					callback(response.status ? response.responseObject : false);
				}
			)
		);
	};

	const createOrder = () => {
		if (isSubmitting) return;
		setIsSubmitting(true); 
		const { selectedAccount, selectedPriceSource } = user;
		let shipTo = shippingAddress;
		const billTo = billingAddress;
		const selectedAccountType = props.getSelectedAccountType();

		const orderCreationPayload = {
			P_OPERATION: 'CREATE',
			P_OPERATING_UNIT: 'GE US Appliances',
			RESPONSIBILITY: 'GEOM_SETUP _US_APL',
			RESP_APPLICATION: 'GEONT',
			SECURITY_GROUP: 'STANDARD',
			NLS_LANGUAGE: 'AMERICAN',
			P_HEADER_REC: {
				OPERATION: 'CREATE',
				ATTRIBUTE1: user.userDetails.sso,
				ATTRIBUTE2: user.userDetails.firstName+' '+user.userDetails.lastName,
				ATTRIBUTE8: props.isInternationalPartsCustomer ? fields.phoneNumber : fields.phoneNumber.replaceAll(/[^0-9]/g, ''),
				BOOKED_FLAG: props.isInternationalPartsCustomer ? 'N' : 'Y',
				REQUEST_DATE: moment(fields.requestedShipDate).format('YYYY-MM-DDT00:00:00'),
				CUST_PO_NUMBER: fields.customerPONumber,
				SHIPPING_INSTRUCTIONS: fields.shippingInstructions.trim(),
				BLANKET_NUMBER: selectedPriceSource.AGREEMENT_NUMBER.toString(),
				ORG_ID: 82,
				ATTRIBUTE15: props.isInternationalPartsCustomer ? 'INTERNATIONAL ORDER' : 'CUSTOMER ORDER',
				INVOICE_TO_ORG_ID: billingAddress.SITE_USE_ID && billingAddress.SITE_USE_ID.toString(),
				SHIP_FROM_ORG_ID: '202', // default to 202 for JEF
				SHIP_TO_ORG_ID: shippingAddress.SITE_USE_ID && shippingAddress.SITE_USE_ID.toString()
			},
			P_OLD_HEADER_REC: {
				REQUEST_DATE: moment(fields.requestedShipDate).format('YYYY-MM-DDT00:00:00')
			},
			P_HEADER_VAL_REC: {
				CUSTOMER_NUMBER: selectedAccount.ACCOUNTNUMBER,
				ORDER_TYPE: 'Standard Sales',
				ORDER_SOURCE: 'PartsNet',
				PRICE_LIST: selectedPriceSource.PRICE_LIST_NAME,
				SHIPMENT_PRIORITY: shippingPriorities[fields.orderType + (fields.shipSet === '1' ? 'T' : 'Z')],
				SHIPPING_METHOD: getShippingMethod().ebsShipMethodName
			},
			P_LINE_TBL: {
				P_LINE_TBL_ITEM: items.map((item, index) => ({
					OPERATION: 'CREATE',
					ORDERED_QUANTITY: +item.quantity,
					LINE_NUMBER: (index + 1).toString(),
					ORDER_QUANTITY_UOM: item.primaryUnitOfMeasure === 'EACH' ? 'EA' : item.primaryUnitOfMeasure,
					ORG_ID: 82,
					USER_ITEM_DESCRIPTION: item.itemDescription,
					[fields.shipSet === '1' ? 'SHIP_SET' : 'SHIP_SET_ID']: '1',
					SHIP_FROM_ORG_ID: item.sourceOrganizationId && item.sourceOrganizationId.toString(),
					ATTRIBUTE9: 'TRUE',
					ATTRIBUTE16: item.originalItemNumber || undefined,
					ATTRIBUTE17: item.originalItemNumber ? 'Superseded' : undefined,
					UNIT_LIST_PRICE: item.unitPrice,
					UNIT_SELLING_PRICE: +item.adjustedUnitPrice,
					ATTRIBUTE4: (item.customerReference || '').trim()
				}))
			},
			P_LINE_VAL_TBL: {
				P_LINE_VAL_TBL_ITEM: items.map(item => ({
					INVENTORY_ITEM: item.itemNumber,
					SHIPPING_METHOD: getShippingMethod().ebsShipMethodName,
					LINE_TYPE: selectedAccountType === 'HQ' ? 'Ship Only' : 'Standard Line'
				}))
			}
		};

		if (props.isInternationalPartsCustomer) {
			const endCustomerId = shippingAddress.SHIP_TO_ACCOUNT_ID ? shippingAddress.SHIP_TO_ACCOUNT_ID : selectedAccount.ACCOUNTID;

			orderCreationPayload.P_HEADER_REC = {
				...orderCreationPayload.P_HEADER_REC,
				END_CUSTOMER_ID: endCustomerId && endCustomerId.toString(),
				END_CUSTOMER_SITE_USE_ID: shippingAddress.SITE_USE_ID && shippingAddress.SITE_USE_ID.toString()
			};

			if (fields.freightForwarder) {
				orderCreationPayload.P_HEADER_REC.SHIP_TO_ORG_ID = fields.freightForwarder.SITE_USE_ID && fields.freightForwarder.SITE_USE_ID.toString();
				shipTo = fields.freightForwarder;
			}
		}

		if (paymentDetailsRef.current.isPaymentConfirmed) {
			const paymentTransactionId = paymentDetailsRef.current.transactionId && paymentDetailsRef.current.transactionId.toString();

			orderCreationPayload.P_HEADER_VAL_REC.PAYMENT_TERM = 'Prepayment';

			orderCreationPayload.P_HEADER_PAYMENT_TBL = {
				P_HEADER_PAYMENT_TBL_ITEM: [
					{
						PAYMENT_AMOUNT: orderTotal,
						PAYMENT_TRX_ID: paymentTransactionId,
						PAYMENT_COLLECTION_EVENT: 'PREPAY',
						PAYMENT_TYPE_CODE: 'CREDIT_CARD',
						OPERATION: 'CREATE'
					}
				]
			};
		}

		orderCreationPayload.UDA_PAYLOAD = {
			apiVersion: '1.0',
			objectName: 'OE_ORDER_HEADERS_ALL',
			rowTable: {
				rowTableTime: [
					{
						rowIdentifier: 9993,
						groupId: 1249,
						groupappId: 660,
						groupType: 'OE_HEADER_ATTRIBUTES_EXT',
						groupName: 'PARTS_MANUAL',
						dataLevel: 'ORDER_HEADER'
					}
				]
			},
			dataTable: {
				dataTableItem: [
					{
						rowIdentifier: 9993,
						name: 'Consol_Pick',
						dispValue: 'Y'
					},
					{
						rowIdentifier: 9993,
						name: 'Customer_Instructions',
						dispValue: fields.customerInstructions.trim()
					},
					{
						rowIdentifier: 9993,
						name: 'Print_Price',
						dispValue: fields.pricesOnPackingSlip
					},
					{
						rowIdentifier: 9993,
						name: 'EMail',
						dispValue: fields.emailId
					}
				]
			},
			colValuePairs: {
				colValuePairsItem: [
					{
						name: 'HEADER_ID'
					}
				]
			},
			clsValuePairs: {
				clsValuePairsItem: [
					{
						name: 'ENTITY',
						value: 'ADMIN_DEFINED'
					}
				]
			}
		};

		dispatch(
			processPartsOrder(orderCreationPayload, 'requestType=createOrder&appName=PNET', true, orderCreationResponse => {
				if (orderCreationResponse.status && orderCreationResponse.responseObject) {
					dispatch(
						saveCreateOrderPayload({
							...createOrderPayload,
							items,
							orderDetails: orderCreationResponse.responseObject,
							shipTo,
							billTo
						})
					);

					let cartPayload = cart.cart.responseObject;

					cartPayload = {
						accountNumber: cartPayload.accountNumber,
						userId: cartPayload.userId,
						createdDate: moment().format(DATE_TIME_FORMAT),
						updatedDate: moment().format(DATE_TIME_FORMAT),
						cartItems: []
					};

					dispatch(
						addToPartsCart(cartPayload, cartResponse => {
							if (cartResponse.status && cartResponse.responseObject) {
								dispatch(saveToCart(cartResponse));
								dispatch(resetAvailabilityToCart());
								dispatch(resetPricesToCart());
								dispatch(updateCartOrderPoints(null));
							} else {
								setIsSubmitting(false); 
								browserHistory.push('/error');
							}
						})
					);

					browserHistory.replace('/order/confirmation');
					}else{
					setIsSubmitting(false); 
				}
			})
		);
	};

	const getParameterByName = name => {
		var regex = new RegExp('[?&]' + name.replace(/[\\]]/g, '\\$&') + '(=([^&#]*)|&|#|$)'),
			results = regex.exec(window.location.href);
		if (!results) return null;
		if (!results[2]) return '';
		return decodeURIComponent(results[2].replace(/\+/g, ' '));
	};

	useEffect(() => {
		if (paymentDetailsRef.current.isPaymentConfirmed) {
			createOrder();
		}
	}, [paymentDetailsRef.current.isPaymentConfirmed]);

	useEffect(() => {
		scrollTo && scrollTo({ top: 0, behavior: 'auto' });

		if (!createOrderPayload) {
			const checkoutBackup = JSON.parse(localStorage.getItem('chkBkp'));
			localStorage.removeItem('chkBkp');

			if (checkoutBackup) {
				const { items, fields, errors, availableFreightForwarders, handlingCharge, taxAmount, billingAddress, shippingAddress } = checkoutBackup;
				setItems(items);
				setFields(fields);
				setErrors(errors);
				setAvailableFreightForwarders(availableFreightForwarders);
				setHandlingCharge(handlingCharge);
				setTaxAmount(taxAmount);
				setBillingAddress(billingAddress);
				setShippingAddress(shippingAddress);

				const transactionId = getParameterByName('TransactionId');
				const email = getParameterByName('Email');
				const paymentError = getParameterByName('error');

				if (!transactionId) {
					if (paymentError) {
						setPaymentError(paymentError);
					} else {
						browserHistory.replace('/cart');
					}
				} else {
					paymentDetailsRef.current.isPaymentConfirmed = true;
					paymentDetailsRef.current.transactionId = transactionId;
					paymentDetailsRef.current.email = email;
					paymentDetailsRef.current.paymentError = paymentError;
				}
			} else {
				browserHistory.replace('/cart');
			}
		} else {
			let { billingAddress } = createOrderPayload;

			if (!billingAddress) {
				const addressList = props.getCombinedAddresses();
				let billingAddressList = JSON.parse(JSON.stringify(addressList));
				if (billingAddressList) {
					if (billingAddressList.find(itm => itm.PURPOSE === 'BILL_TO')) {
						billingAddressList = billingAddressList.filter(itm => itm.PURPOSE === 'BILL_TO');
					} else {
						billingAddressList = billingAddressList.filter(itm => itm.PURPOSE === 'SOLD_TO');
					}
				}
				billingAddress = billingAddressList && billingAddressList.length > 0 && billingAddressList[0];
				billingAddress = {
					...billingAddress,
					NAME: user && user.selectedAccount && user.selectedAccount.ORGANIZATIONNAME
					// POSTALCODE: billingAddress.POSTALCODE && billingAddress.POSTALCODE.split('-')[0]
				};
			}

			setBillingAddress(billingAddress);
		}
	}, []);

	useEffect(() => {
		if (!paymentDetailsRef.current.isPaymentConfirmed) {
			if (!shippingAddress && user.selectedAddress) {
				let { shippingAddress } = createOrderPayload || {};

				if (!shippingAddress) {
					const selectedAddress = JSON.parse(user.selectedAddress);
					shippingAddress = {
						...selectedAddress,
						NAME: selectedAddress.LOCATION === 'NEW_SHIP_TO_PNET' ? selectedAddress.NAME : user && user.selectedAccount && user.selectedAccount.ORGANIZATIONNAME
						// POSTALCODE: selectedAddress.POSTALCODE && selectedAddress.POSTALCODE.split('-')[0]
					};
				}

				setShippingAddress(shippingAddress);
			} else {
				browserHistory.replace('/cart');
			}
		}
	}, [user.selectedAddress]);

	const validatePrice = () => {
		const itemsWithPricingIssues = [];
		items.forEach(item => {
			if (item.extendedPrice === null && !itemsWithPricingIssues.includes(item.itemNumber)) {
				itemsWithPricingIssues.push(item.itemNumber);
			}
		});

		if (itemsWithPricingIssues.length) {
			setError({
				title: 'Action',
				content: `Unable to order item(s) - <strong>${itemsWithPricingIssues.join(
					', '
				)}</strong> at this moment. If you would like to continue with this order, please remove this item(s) and try again.`,
				buttons: ['ok'],
				onConfirm: () => setError(null)
			});

			return false;
		} else {
			return true;
		}
	};

	useEffect(() => {
		if (!paymentDetailsRef.current.isPaymentConfirmed && items.length && billingAddress && shippingAddress && validatePrice()) {
			const { selectedAccount } = user;
			const taxLines = items.reduce((lines, item) => {
					// Main item
					lines.push({
						...item,
						isBagItem: false
					});

					// Bag items
					(item.bagItems || [])
                    .filter(bagItem => bagItemVisibility[`${item.id}-${bagItem.name}`])
                    .forEach(bagItem => {
                        lines.push({
                            ...bagItem,
                            itemNumber: bagItem.name,
                            extendedPrice: bagItem.extendedPrice,
                            quantity: bagItem.quantity,
                            isBagItem: true
                        });
                    });

					return lines;
				}, []);

			const taxPayload = {
				CURRENCY_CODE: 'USD',
				CUSTOMER_NUMBER: selectedAccount.ACCOUNTNUMBER,
				INVOICE_DATE: moment().format('YYYYMMDD'),
				INVOICE_NUMBER: 'INVOICE',
				LINES: taxLines.map((item, index) => {
					return {
						LINE_ID: (index + 1).toString(),
						LINE_NUMBER: (index + 1).toString(),
						// UNIQUE_LINE_NUMBER: '12522830',
						GROSS_AMOUNT: item.extendedPrice,
						BILL_TO_COUNTRY: billingAddress.COUNTRY,
						BILL_TO_STATE: billingAddress.COUNTRY === 'US' ? billingAddress.STATE : billingAddress.STATE || billingAddress.PROVINCE,
						BILL_TO_CITY: billingAddress.CITY,
						BILL_TO_POSTCODE: billingAddress.POSTALCODE ? billingAddress.POSTALCODE.split('-')[0] : '0000',
						// BILL_TO_GEOCODE: '2927',
						// IS_EXEMPT: 'N',
						// IS_NO_TAX: 'N',
						// MOVEMENT_DATE: '20190429',
						// ORDER_ACCEPTANCE_COUNTRY: 'US',
						// ORDER_ACCEPTANCE_POSTCODE: '06828',
						// ORDER_ORIGIN_COUNTRY: 'US',
						// ORDER_ORIGIN_POSTCODE: '06828',
						PART_NUMBER: item.itemNumber,
						// PRODUCT_CODE: '52141546',
						// SHIP_FROM_COUNTRY: 'US',
						// SHIP_FROM_PROVINCE: 'ONTARIO',
						// SHIP_FROM_STATE: 'TX',
						// SHIP_FROM_COUNTY: 'TARRANT',
						// SHIP_FROM_CITY: 'FORT WORTH',
						// SHIP_FROM_POSTCODE: '76102',
						SHIP_TO_COUNTRY: shippingAddress.COUNTRY,
						SHIP_TO_PROVINCE: shippingAddress.PROVINCE,
						SHIP_TO_STATE: shippingAddress.STATE,
						SHIP_TO_CITY: shippingAddress.CITY,
						SHIP_TO_POSTCODE: shippingAddress.POSTALCODE ? shippingAddress.POSTALCODE.split('-')[0] : '0000',
						// SHIP_TO_GEOCODE: '2925',
						// TRANSACTION_TYPE: 'GS',
						// UNIT_OF_MEASURE: 'EA',
						// TAX_DETERMINATION_DATE: '20190529',
						QUANTITY: item.quantity.toString()
						// UOM: 'EA'
					};
				})
			};

			dispatch(
				getTax(
					taxPayload,
					`requestType=getTax`,
					taxResponse => {
						if (
							taxResponse &&
							taxResponse.responseObject &&
							taxResponse.responseObject.value &&
							taxResponse.responseObject.value.invoice &&
							taxResponse.responseObject.value.invoice.line
						) {
							const taxAmount = taxResponse.responseObject.value.invoice.line.reduce((acc, cur) => acc + cur.totaltaxamount, 0);
							setTaxAmount(taxAmount);
						}
					},
					true
				)
			);
		}
		}, [items, billingAddress, shippingAddress, bagItemVisibility]);

	useEffect(() => {
		const bagChecks = [];

		items.forEach(item => {
			(item.bagItems || []).forEach(bagItem => {
				bagChecks.push({ itemId: item.id, bagItem });
			});
		});

		bagChecks.forEach(({ itemId, bagItem }) => {
			checkPriceSubTypeExcluded(bagItem.priceSubType, isExcluded => {
				setBagItemVisibility(prev => ({
					...prev,
					[`${itemId}-${bagItem.name}`]: isExcluded === false
				}));
			});
		});
	}, [items]);

	useEffect(() => {
		if (paymentDetailsRef.current.isReadyForPayment) {
			paymentDetailsRef.current.isReadyForPayment = false;

			const backupData = {
				items,
				fields,
				availableFreightForwarders,
				errors,
				billingAddress,
				shippingAddress,
				handlingCharge,
				taxAmount,
				orderTotal
			};

			localStorage.setItem('chkBkp', JSON.stringify(backupData));

			document.payment_confirmation.accessKey = paymentDetails.access_key;
			document.payment_confirmation.action = cyberSourceURL;
			document.payment_confirmation.target = '_self';
			document.payment_confirmation.submit();
		}
	}, [paymentDetails]);

	const isEmailIdValid = emailId => /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,5}$/.test(emailId);

	const isPhoneNumberValid = phoneNumber => {
		if (props.isInternationalPartsCustomer) {
			return /^\+?[0-9]{1,20}$/.test(phoneNumber.trim());
		}
		return /^1?[2-9][0-9]{9}$/.test(phoneNumber.replace(/\D+/g, ''));
	};

	const isFieldValid = (fieldName, fieldValue) => {
		if (['customerPONumber', 'requestedShipDate'].includes(fieldName)) {
			if (fieldValue.trim() === '') {
				return false;
			}
		} else if (fieldName === 'phoneNumber') {
			if (fieldValue.trim() === '' || !isPhoneNumberValid(fieldValue)) {
				return false;
			}
		} else if (fieldName === 'emailId') {
			if (fieldValue.trim() === '' || !isEmailIdValid(fieldValue)) {
				return false;
			}
		}

		return true;
	};

	const handleChange = e => {
		const { name: fieldName, value: fieldValue } = e.target;

		setFields(fields => ({
			...fields,
			[fieldName]: fieldName === 'customerPONumber' ? fieldValue.toUpperCase() : fieldValue
		}));

		const isValid = isFieldValid(fieldName, fieldValue);

		if (isValid && errors[fieldName]) {
			setErrors(errors => ({
				...errors,
				[fieldName]: false
			}));
		}
	};

	const handleBlur = e => {
		const { name: fieldName, value: fieldValue } = e.target;
		let isValid;
		if (fieldName === 'phoneNumber') {
			const phoneNumber = props.normalizePhoneNumber(fieldValue, props.isInternationalPartsCustomer);
			isValid = isFieldValid(fieldName, phoneNumber);
			setFields(fields => ({
				...fields,
				phoneNumber
			}));
		} else {
			isValid = isFieldValid(fieldName, fieldValue);
		}

		setErrors(errors => ({
			...errors,
			[fieldName]: !isValid
		}));
	};

	const handleRemoveItem = (id, event) => {
		event.preventDefault();
		let newItems = items.filter(item => item.id !== id);
		const { selectedAccount, userDetails } = user;

		let cartPayload = cart.cart.responseObject;
		const cartItems = cartPayload.cartItems.filter(cartItem => cartItem.buyerLineNo !== id);

		cartPayload = {
			...cartPayload,
			accountNumber: selectedAccount.ACCOUNTNUMBER,
			createdDate: moment().format('MM/DD/YYYY'),
			updatedDate: moment().format('MM/DD/YYYY'),
			userId: userDetails.sso,
			cartItems
		};

		dispatch(
			addToPartsCart(cartPayload, response => {
				if (response.status && response.responseObject) {
					dispatch(saveToCart(response));

            if (newItems.length > 0) {
              const {
                selectedAccount: { ACCOUNTNUMBER: accountNumber },
							selectedPriceSource: { 'Sub Account Classification': subAccountClassification },
							selectedAddress: selectedAddressString
              } = user;
              const zipCode = props.customZipCode;
              const selectedAddress = JSON.parse(selectedAddressString || "{}");
              // const soldToAddress = props.getAddressByCategory('SOLD_TO');
              // const soldToSiteNumber = soldToAddress && soldToAddress.SITENUMBER;
              const billToAddress = props.getAddressByCategory("BILL_TO");
              // const billToSiteNumber = billToAddress && billToAddress.SITENUMBER;
              // const agreementNumber = user.selectedPriceSource.AGREEMENT_NUMBER;

              let shipToSiteNumber = selectedAddress.SITENUMBER;
						let shipToAccountNumber = selectedAddress.SHIP_TO_ACCOUNT_NUMBER || accountNumber;
              if (!shipToSiteNumber) {
							const shipToAddress = props.getAddressByCategory('SHIP_TO');

                shipToSiteNumber = shipToAddress && shipToAddress.SITENUMBER;
                if (shipToAddress.SHIP_TO_ACCOUNT_NUMBER) {
                  shipToAccountNumber = shipToAddress.SHIP_TO_ACCOUNT_NUMBER;
                }
              }

						const itemsBySalesAgreement = _.groupBy(newItems, 'agreementNumber');

              //for now it's one request and one sales agreement for all items in an order

              const shipMethodDetails = shippingMethods[
                props.isInternationalPartsCustomer
                  ? "international"
                  : "domestic"
              ][orderType === "R" ? "regular" : "emergency"].find(
                (shippingMethodItem) =>
                  shippingMethodItem.displayName === shippingMethod,
              );

              const pricingPayload = {
                priceRequest: {
                  priceRequestType: "PARTS ONLY",
                  headerPricingDate: moment().format("DD-MMM-YYYY"),
                  customerPartyAccountNumber: accountNumber,
                  orderLevelModInd: "Y",
                  shipToState: selectedAddress.STATE,
                  orderType: "Standard Sales",
                  orderReference: props.isInternationalPartsCustomer
                    ? "INTERNATIONAL ORDER"
                    : "CUSTOMER ORDER",
                  shipToZipcode: zipCode,
                  custClass: subAccountClassification.slice(-3),
                  requestedDeliveryDate: moment(
                    fields.requestedShipDate,
                  ).format("DD-MMM-YYYY"),
                  source: "PartsNet",
                  serviceLevel: shipMethodDetails.displayName,
                  shipmentCompCode: `${fields.orderType}${fields.shipSet === "1" ? "T" : "Z"}-${shipMethodDetails.code}`,
                  shipmentOrderType: fields.orderType,
                  headerShipmentMethod: shipMethodDetails.ebsShipMethodName,
                  shipmentPriorityCode: `${fields.orderType}${fields.shipSet === "1" ? "T" : "Z"}`,
                  billToPartySite: billToAddress && billToAddress.SITE_USE_ID,
                  shipToPartySite: selectedAddress.SITE_USE_ID,
                  lineItems: [],
                },
              };

              for (const agreementNumber in itemsBySalesAgreement) {
                let orderSubtotal = 0;
                pricingPayload.priceRequest.lineItems = itemsBySalesAgreement[
                  agreementNumber
                ].map((item, index) => {
                 orderSubtotal += item.quantity * item.adjustedUnitPrice;

                  return {
                    lineNo: pricingPayload.priceRequest.lineItems.length + 1,
                    lineType: "PART",
                    item: item.itemNumber,
                    qty: item.quantity,
                    // "linePricingDate": null,
                    salesAgreementNumber: agreementNumber.toString(),
                    // "shipmentMethod": null,
                    warehouseOrgCode: item.warehouseCode,
                    // "unCratedInd": null,
                    // "grossPointValue": null,
                    netItemIndicator: item.isVolumeDiscountEligible ? "N" : "Y",
                    // "oversizePart": "Y"
                  };
                });

                pricingPayload.priceRequest.orderTotal = orderSubtotal;
              }

              // make pricing service call
              dispatch(
							getOnlinePricingForParts(pricingPayload, `requestType=priceOrder&${PNET_APP_QUERY_PARAM}`, onlinePricingResponse => {
								if (onlinePricingResponse.status && onlinePricingResponse.responseObject) {
                      newItems = newItems.map((item, index) => {
                        const onlinePricingData =
                          onlinePricingResponse.responseObject &&
                          onlinePricingResponse.responseObject.priceResponse &&
                          onlinePricingResponse.responseObject.priceResponse.find(
                            (pricingItem) =>
                              pricingItem.item === item.itemNumber &&
                              pricingItem.lineIndex.toString() ===
                                (index + 1).toString(),
                          );

                        return {
                          ...item,
                        adjustedUnitPrice: onlinePricingData ? onlinePricingData.salesPrice : item.adjustedUnitPrice,
						extendedPrice: onlinePricingData ? onlinePricingData.extendedPrice : item.extendedPrice
                        };
                      });

                      setItems(newItems);
                      setHandlingCharge(
                        onlinePricingResponse.responseObject
                          .deliveryHandlingCharges,
                      );
                    }
							})
						);
					} else {
						browserHistory.replace('/cart');
					}
				} else {
					browserHistory.push('/error');
				}
			})
		);
	};

	const handleBackToCart = () => {
		browserHistory.replace('/cart');
	};

	const isFormValid = () => {
		for (const fieldName in fields) {
			const isValid = isFieldValid(fieldName, fields[fieldName]);

			if (!isValid && !errors[fieldName]) {
				setErrors(errors => ({
					...errors,
					[fieldName]: true
				}));
			}

			if (!isValid) {
				return false;
			}
		}
		return true;
	};

	const handlePaymentByCreditCard = () => {
		if (!isFormValid()) {
			scrollTo && scrollTo({ top: 0, behavior: 'auto' });
			return;
		}

		const {
			userDetails: { email, firstName, lastName }
		} = user;

		let signed_date_time = new Date().toISOString();
		signed_date_time = signed_date_time.substring(0, signed_date_time.indexOf('.'));
		signed_date_time += 'Z';

		paymentDetailsRef.current = {
			...paymentDetailsRef.current,
			transaction_uuid: uuidv4(),
			signed_date_time,
			amount: orderTotal,
			bill_to_email: email,
			bill_to_forename: firstName,
			bill_to_middleinitial: '',
			bill_to_surname: lastName,
			bill_to_address_line1: billingAddress.ADDRESS1,
			bill_to_address_line2: billingAddress.ADDRESS2 || '',
			bill_to_address_city: billingAddress.CITY,
			bill_to_address_state: billingAddress.STATE,
			bill_to_address_postal_code: billingAddress.POSTALCODE,
			reference_number: new Date().getTime() + '-1001'
		};

		const {
			amount,
			bill_to_address_postal_code,
			transaction_uuid,
			signed_field_names,
			locale,
			transaction_type,
			bill_to_email,
			bill_to_address_country,
			bill_to_surname,
			bill_to_middleinitial,
			bill_to_address_line2,
			bill_to_address_line1,
			skip_decision_manager,
			profile_id,
			access_key,
			bill_to_address_city,
			currency,
			bill_to_forename,
			// signed_date_time,
			payment_method,
			override_custom_receipt_page,
			override_custom_cancel_page,
			reference_number,
			bill_to_company_name,
			bill_to_address_state
		} = paymentDetailsRef.current;

		dispatch(
			getCyberSource(
				'amount=' +
					amount +
					'&bill_to_address_postal_code=' +
					bill_to_address_postal_code +
					'&bill_to_company_name=' +
					bill_to_company_name +
					'&bill_to_address_state=' +
					bill_to_address_state +
					'&transaction_uuid=' +
					transaction_uuid +
					'&signed_field_names=' +
					signed_field_names +
					'&locale=' +
					locale +
					'&transaction_type=' +
					transaction_type +
					'&bill_to_email=' +
					bill_to_email +
					'&bill_to_address_country=' +
					bill_to_address_country +
					'&bill_to_surname=' +
					bill_to_surname +
					'&bill_to_middleinitial=' +
					bill_to_middleinitial +
					'&bill_to_address_line2=' +
					bill_to_address_line2 +
					'&bill_to_address_line1=' +
					bill_to_address_line1 +
					'&profile_id=' +
					profile_id +
					'&access_key=' +
					access_key +
					'&bill_to_address_city=' +
					bill_to_address_city +
					'&currency=' +
					currency +
					'&bill_to_forename=' +
					bill_to_forename +
					'&signed_date_time=' +
					signed_date_time +
					'&payment_method=' +
					payment_method +
					'&override_custom_receipt_page=' +
					override_custom_receipt_page +
					'&override_custom_cancel_page=' +
					override_custom_cancel_page +
					'&reference_number=' +
					reference_number +
					'&skip_decision_manager=' +
					skip_decision_manager,
				response => {
					if (response.status) {
						setPaymentDetails({
							...paymentDetailsRef.current,
							signature: response.responseObject.signature,
							access_key: response.responseObject.access_key,
							profile_id: response.responseObject.profile_id,
							transaction_uuid: response.responseObject.transaction_uuid,
							signed_field_names: response.responseObject.signed_field_names,
							signed_date_time: response.responseObject.signed_date_time,
							locale: response.responseObject.locale,
							transaction_type: response.responseObject.transaction_type,
							// reference_number: response.responseObject.reference_number,
							payment_method: response.responseObject.payment_method,
							bill_to_address_country: response.responseObject.bill_to_address_country
						});

						paymentDetailsRef.current.isReadyForPayment = true;
					} else if (response.error || !response.status) {
						setError(response.responseMessage);
					}
				}
			)
		);
	};

	const handleOrderCreation = (isCardPayment, event) => {
		event.preventDefault();

		if (!validatePrice()) {
			return;
		}
		if (!isFormValid()) {
			scrollTo && scrollTo({ top: 0, behavior: 'auto' });
			return;
		}

		if (isCardPayment) {
			handlePaymentByCreditCard();
		} else {
			if (user.selectedAccount.PAYMENTTERMS === 'Prepayment') {
				setDialog({
					id: 'forcePrepaymentModal',
					title: 'Payment Terms',
					content: 'Since your payment terms is prepayment, please proceed with Pay by Credit Card option',
					onClose: () => setDialog(null)
				});
			} else {
				createOrder();
			}
		}
	};

	const { ACCOUNTNUMBER: accountNumber, ORGANIZATIONNAME: customerName } = user.selectedAccount;
	const { AGREEMENT_NUMBER: salesAgreementNumber, AGREEMENT_NAME: salesAgreementName } = user.selectedPriceSource;

	return (
		<div className="pNetCheckout">
			<div className="smallPush">
				<h1 className="pageTitle">Checkout</h1>
				 {paymentError && <p className="bold red">{paymentError}</p>}
			</div>
			<form>
				{/* Billing & Shipping */}
				<CollapsibleSection title="Shipping & Billing">
					<React.Fragment>
						<dl>
							<dt>
								<label htmlFor="customerPONumber">
									<b>
										Customer PO <span className="required">*</span>
									</b>
								</label>
							</dt>
							<dd className={errors.customerPONumber ? 'error' : undefined}>
								<input id="customerPONumber" type="text" name="customerPONumber" value={fields.customerPONumber} onChange={handleChange} onBlur={handleBlur} />
								{errors.customerPONumber && <p className="message">Please enter PO Number</p>}
							</dd>
							<dt>
								<b>Customer Account</b>
							</dt>
							<dd>{accountNumber}</dd>
							<dt>
								<b>Sales Agreement Number</b>
							</dt>
							<dd>{salesAgreementNumber}</dd>
							<dt>
								<b>Ship To</b>
							</dt>
							<dd>
								{shippingAddress && (
									<p>
										{shippingAddress.NAME && (
											<React.Fragment>
												{shippingAddress.NAME}
												<br />
											</React.Fragment>
										)}
										{shippingAddress.ADDRESS1 && (
											<React.Fragment>
												{shippingAddress.ADDRESS1}
												<br />
											</React.Fragment>
										)}
										{shippingAddress.ADDRESS2 && (
											<React.Fragment>
												{shippingAddress.ADDRESS2}
												<br />
											</React.Fragment>
										)}
										{shippingAddress.ADDRESS3 && (
											<React.Fragment>
												{shippingAddress.ADDRESS3}
												<br />
											</React.Fragment>
										)}
										{shippingAddress.CITY}, {shippingAddress.STATE || shippingAddress.PROVINCE}, {shippingAddress.COUNTRY}, {shippingAddress.POSTALCODE}
									</p>
								)}
							</dd>
							{props.isInternationalPartsCustomer && (
								<React.Fragment>
									<dt>
										<b>Send Order to Freight Forwarder?</b>
									</dt>
									<dd>{fields.freightForwarder ? 'Yes' : 'No'}</dd>
								</React.Fragment>
							)}
							<dt>
								<label htmlFor="phoneNumber">
									<b>
										Phone Number <span className="required">*</span>
									</b>
								</label>
							</dt>
							<dd className={errors.phoneNumber ? 'error' : undefined}>
								<input id="phoneNumber" type="text" name="phoneNumber" value={fields.phoneNumber} onChange={handleChange} onBlur={handleBlur} />
								{errors.phoneNumber && fields.phoneNumber === '' ? (
									<p className="message">Please enter a phone number</p>
								) : (
									errors.phoneNumber &&
									fields.phoneNumber !== '' &&
									!props.isValidPhoneNumber(fields.phoneNumber) && (
										<p className="message">
											{props.isInternationalPartsCustomer
												? 'Please enter a valid phone number'
												: 'Please enter a valid 10-digit phone number, starting with the area code'}
										</p>
									)
								)}
							</dd>
						</dl>
						<dl>
							<dt>
								<b>Customer Name</b>
							</dt>
							<dd>{customerName || ''}</dd>
							<dt>
								<label htmlFor="emailId">
									<b>
										Email Address <span className="required">*</span>
									</b>
								</label>
							</dt>
							<dd className={errors.emailId ? 'error' : undefined}>
								<input id="emailId" type="text" name="emailId" value={fields.emailId} onChange={handleChange} onBlur={handleBlur} />
								{errors.emailId && <p className="message">Please enter a valid email Id</p>}
							</dd>
							<dt>
								<b>Sales Agreement Name</b>
							</dt>
							<dd>{salesAgreementName}</dd>
							<dt>
								<b>Bill To</b>
							</dt>
							<dd>
								{billingAddress && (
									<p>
										{billingAddress.NAME}
										<br />
										{billingAddress.ADDRESS1}
										<br />
										{billingAddress.ADDRESS2 && (
											<React.Fragment>
												{billingAddress.ADDRESS2}
												<br />
											</React.Fragment>
										)}
										{billingAddress.CITY}, {billingAddress.STATE || billingAddress.PROVINCE}, {billingAddress.COUNTRY}, {billingAddress.POSTALCODE}
									</p>
								)}
							</dd>
							{props.isInternationalPartsCustomer && fields.freightForwarder && (
								<React.Fragment>
									<dt>
										<b>Freight Forwarder</b>
									</dt>
									<dd>
										<p>
											{fields.freightForwarder.NAME}
											<br />
											{fields.freightForwarder.ADDRESS1}
											<br />
											{fields.freightForwarder.ADDRESS2 && (
												<React.Fragment>
													{fields.freightForwarder.ADDRESS2}
													<br />
												</React.Fragment>
											)}
											{fields.freightForwarder.CITY}, {fields.freightForwarder.STATE || fields.freightForwarder.PROVINCE}, {fields.freightForwarder.COUNTRY},{' '}
											{fields.freightForwarder.POSTALCODE}
										</p>
									</dd>
								</React.Fragment>
							)}
						</dl>
					</React.Fragment>
				</CollapsibleSection>
				{/* Order Details */}
				<CollapsibleSection title="Order Details">
					<React.Fragment>
						<dl>
							<dt>
								<b>Order Type</b>
							</dt>
							<dd>{fields.orderType === 'R' ? 'Regular' : fields.orderType === 'E' ? 'Emergency' : ''}</dd>
							<dt>
								<b>Shipment Profile</b>
							</dt>
							<dd>{fields.shipSet === '1' ? 'Single Shipment' : 'Unlimited Shipment'}</dd>
							<dt>
								<b>Requested Ship Date</b>
								<br />
								<em className="small">(mm/dd/yyyy)</em>
							</dt>
							<dd>{moment(fields.requestedShipDate).format('MM/DD/YYYY')}</dd>
						</dl>
						<dl>
							<dt>
								<label htmlFor="shippingMethod">
									<b>Ship Method</b>
								</label>
							</dt>
							<dd>{shippingMethod}</dd>
							<dt>
								<label htmlFor="pricesOnPackingSlip">
									<b>Prices on Packing Slip</b>
								</label>
							</dt>
							<dd>
								<select id="pricesOnPackingSlip" name="pricesOnPackingSlip" value={fields.pricesOnPackingSlip} onChange={handleChange}>
									<option value="Y">Yes</option>
									<option value="N">No</option>
								</select>
							</dd>
						</dl>
					</React.Fragment>
				</CollapsibleSection>
				{/* Instructions */}
				<CollapsibleSection title="Instructions">
					<React.Fragment>
						<dl>
							<dt>
								<label htmlFor="customerInstructions">
									<b>Customer Instructions</b>
								</label>
							</dt>
							<dd>
								<textarea
									id="customerInstructions"
									name="customerInstructions"
									rows={4}
									maxLength={150}
									value={fields.customerInstructions}
									onChange={handleChange}
								/>
							</dd>
						</dl>
						<dl>
							<dt>
								<label htmlFor="shippingInstructions">
									<b>Shipping Instructions</b>
								</label>
							</dt>
							<dd>
								<textarea
									id="shippingInstructions"
									name="shippingInstructions"
									rows={4}
									maxLength={150}
									value={fields.shippingInstructions}
									onChange={handleChange}
								/>
							</dd>
						</dl>
					</React.Fragment>
				</CollapsibleSection>
				{/* Review Items */}
				<CollapsibleSection title="Review Items">
					<React.Fragment>
						<table className="pNetCheckoutReviewTable dataTableType2 milktoast small">
							<thead>
								<tr>
									{!paymentDetailsRef.current.isPaymentConfirmed && <th>✗</th>}
									<th className="text-left">Item</th>
									<th>Original Item</th>
									<th>Item Description</th>
									<th>Sales Agreement</th>
									<th>Requested Qty</th>
									<th>Qty Available</th>
									<th>Whse</th>
									<th>ETA</th>
									<th>Book Price</th>
									<th>Selling Price</th>
									<th>Volume Discount Eligible</th>
									<th>Extended Price</th>
									<th>Customer Reference</th>
								</tr>
							</thead>
							<tbody>
								{items &&
									items.length > 0 &&
									items.map((item, index) => {
										const itemData = {
											availableQuantity: item.availableQuantity === 1410065408 ? 0 : item.availableQuantity
										};

										return (
											<React.Fragment key={item.id}>
											<tr className={item.extendedPrice === null ? 'error' : undefined} key={item.id}>
												{!paymentDetailsRef.current.isPaymentConfirmed && (
													<td>
														<a className="deleteLink" href="" onClick={handleRemoveItem.bind(this, item.id)}>
															✗
														</a>
													</td>
												)}
												<td className="pNetCheckoutItemNumber text-left">{item.itemNumber}</td>
												<td className="text-left">{item.originalItemNumber}</td>
												<td>{item.itemDescription}</td>
												<td>{item.agreementNumber}</td>
												<td>{item.quantity}</td>
												<td className={itemData.availableQuantity === 0 && !item.eta ? ' red' : ''}>
													{/* {item.availableQuantity} */}
													{itemData.availableQuantity === 0 ? (
														<React.Fragment>
															{itemData.availableQuantity}
															{item.eta && (
																<strong className="block" style={{ fontSize: '.5rem', marginTop: '.25rem', color: '#7a7a7a' }}>
																	BACKORDER
																</strong>
															)}
														</React.Fragment>
													) : (
														itemData.availableQuantity
													)}
												</td>
												<td>{item.warehouseCode}</td>
												<td>{item.quantity > itemData.availableQuantity && item.eta && moment(item.eta).format('MM/DD/YYYY')}</td>
												<td>{props.formatMoney(parseFloat(item.unitPrice || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td>{props.formatMoney(parseFloat(item.adjustedUnitPrice || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td>{item.isVolumeDiscountEligible ? 'Yes' : 'No'}</td>
												<td>{props.formatMoney(parseFloat(item.extendedPrice || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td>{item.customerReference}</td>
											</tr>
										{(item.bagItems || [])
										.filter(bagItem => bagItemVisibility[`${item.id}-${bagItem.name}`])
										.map(bagItem => (
											<tr className="pNetCheckoutBagItemRow" key={`${item.id}-${bagItem.name}`}>
												{!paymentDetailsRef.current.isPaymentConfirmed && <td></td>}
												<td className="text-left" style={{ paddingLeft: '1.5rem', color: '#7a7a7a' }}>
													{bagItem.name}
												</td>
												<td></td>
												<td></td>
												<td></td>
												<td >{bagItem.quantity}</td>
												<td></td>
												<td></td>
												<td></td>
												<td>{props.formatMoney(parseFloat(bagItem.price || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td>{props.formatMoney(parseFloat(bagItem.price || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td></td>
												<td>{props.formatMoney(parseFloat(bagItem.extendedPrice || 0).toFixed(2), 0, '.', ',', false, '$')}</td>
												<td></td>
											</tr>
										))}
								</React.Fragment>)
								})}
							</tbody>
						</table>
						<table className="subtotalTable orderReviewSubtotalTable" style={{ width: 'min(100%, 15rem)' }}>
							<tbody>
								<tr className="subtotal">
									<td>Subtotal:</td>
									<td className="price" style={{ textAlign: 'right' }}>
										{props.formatMoney(cartSubTotal.toFixed(2), 0, '.', ',', false, '$')}
									</td>
								</tr>
								<tr className="handling">
									<td>Freight & Handling:</td>
									<td className="price" style={{ textAlign: 'right' }}>
										{props.formatMoney(handlingCharge || 0, 0, '.', ',', false, '$')}
									</td>
								</tr>
								<tr className="tax">
									<td>Tax:</td>
									<td className="price" style={{ textAlign: 'right' }}>
										{props.formatMoney((taxAmount || 0).toFixed(2), 0, '.', ',', false, '$')}
									</td>
								</tr>
								{/* <tr className="discounts">
									<td>Discounts:</td>
									<td className="price" style={{ textAlign: 'right' }}>
										{props.formatMoney(discount || 0, 0, '.', ',', false, '$')}
									</td>
								</tr> */}
								<tr className="total">
									<td>Order Total:</td>
									<td className="price" style={{ textAlign: 'right' }}>
										{props.formatMoney(orderTotal, 0, '.', ',', false, '$')}
									</td>
								</tr>
							</tbody>
						</table>
					</React.Fragment>
				</CollapsibleSection>
				<div className="flex items-center justify-between">
					<p>
						<button type="button" className="orangeButton uppercase" onClick={handleBackToCart}>
							Back to Cart
						</button>
					</p>
					{hasCreateOrderAccess && hasPricingAccess && (
						<p>
							{user.userDetails.typeOfUser !== 'employee' && (
								<a
									href=""
									className="withIcon withCreditCardIcon noUnderline uppercase bold"
									onClick={handleOrderCreation.bind(this, true)}
									style={{ marginRight: '1rem' }}
								>
									Pay by Credit Card
								</a>
							)}
							<button id="createOrderSubmit" type="button" className="blueButton" onClick={handleOrderCreation.bind(this, false)} disabled={isSubmitting}>
								 {isSubmitting ? 'Booking Order...' : 'Book Order'}
							</button>
						</p>
					)}
				</div>
			</form>
			<form id="payment_confirmation" name="payment_confirmation" method="post">
				<input type="hidden" id="signature" name="signature" value={paymentDetails.signature} />
				<input type="hidden" name="access_key" value={paymentDetails.access_key} />
				<input type="hidden" name="profile_id" value={paymentDetails.profile_id} />
				<input type="hidden" id="transaction_uuid" name="transaction_uuid" value={paymentDetails.transaction_uuid} />
				<input type="hidden" id="signed_field_names" name="signed_field_names" value={paymentDetails.signed_field_names} />
				<input type="hidden" id="signed_date_time" name="signed_date_time" value={paymentDetails.signed_date_time} />
				<input type="hidden" id="locale" name="locale" value={paymentDetails.locale} />
				<input type="hidden" id="transaction_type" name="transaction_type" value={paymentDetails.transaction_type} />
				<input type="hidden" id="reference_number" name="reference_number" value={paymentDetails.reference_number} />
				<input type="hidden" id="skip_decision_manager" name="skip_decision_manager" value={paymentDetails.skip_decision_manager} />
				<input type="hidden" id="amount" name="amount" value={paymentDetails.amount} />
				<input type="hidden" id="currency" name="currency" value={paymentDetails.currency} />
				<input type="hidden" id="payment_method" name="payment_method" value={paymentDetails.payment_method} />
				<input type="hidden" id="bill_to_forename" name="bill_to_forename" value={paymentDetails.bill_to_forename} />
				<input type="hidden" id="bill_to_surname" name="bill_to_surname" value={paymentDetails.bill_to_surname} />
				<input type="hidden" id="bill_to_middleinitial" name="bill_to_middleinitial" value={paymentDetails.bill_to_middleinitial} />
				<input type="hidden" id="bill_to_email" name="bill_to_email" value={paymentDetails.bill_to_email} />
				<input type="hidden" id="bill_to_address_line1" name="bill_to_address_line1" value={paymentDetails.bill_to_address_line1} />
				<input type="hidden" id="bill_to_address_line2" name="bill_to_address_line2" value={paymentDetails.bill_to_address_line2} />
				<input type="hidden" id="bill_to_address_city" name="bill_to_address_city" value={paymentDetails.bill_to_address_city} />
				<input type="hidden" id="bill_to_address_state" name="bill_to_address_state" value={paymentDetails.bill_to_address_state} />
				<input type="hidden" id="bill_to_address_country" name="bill_to_address_country" value={paymentDetails.bill_to_address_country} />
				<input type="hidden" id="bill_to_address_postal_code" name="bill_to_address_postal_code" value={paymentDetails.bill_to_address_postal_code} />
				<input type="hidden" id="bill_to_company_name" name="bill_to_company_name" value={paymentDetails.bill_to_company_name} />
				<input type="hidden" id="override_custom_receipt_page" name="override_custom_receipt_page" value={paymentDetails.override_custom_receipt_page} />
				<input type="hidden" id="override_custom_cancel_page" name="override_custom_cancel_page" value={paymentDetails.override_custom_cancel_page} />
			</form>
			{error && (
				<GenericOverlayDialog
					genericDialogHeading={error.title}
					genericDialogText={error.content}
					extraParagraph={error.extraContent}
					buttons={error.buttons}
					handleOk={error.onConfirm}
					showDialog={true}
				/>
			)}
			{dialog && (
				<GenericOverlayDialog
					flag={dialog.id}
					genericDialogHeading={dialog.title}
					genericDialogText={dialog.content}
					buttons={['ok']}
					handleDialog={dialog.onClose}
					showDialog
				/>
			)}
		</div>
	);
};

export default PartsNetCheckout;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/Order/partsNetCheckout.jsx